import type { FieldGroup } from './types.js';

/**
 * Highest qualification options — the shared education-level vocabulary
 * ({@link EDUCATION_LEVEL_VALUES} in shared-enums), identical to the
 * dating profile's `educationLevel` so the two sync as a direct copy.
 */
export const qualificationOptions = [
  { value: 'below_10th', label: 'Below 10th' },
  { value: 'class_10', label: 'Class 10 (Secondary)' },
  { value: 'class_12', label: 'Class 12 (Higher Secondary)' },
  { value: 'iti_vocational', label: 'ITI / Vocational Training' },
  { value: 'diploma', label: 'Diploma / Polytechnic' },
  { value: 'in_college', label: 'Currently in College' },
  { value: 'associate', label: 'Associate Degree' },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'in_grad_school', label: 'Currently in Grad School' },
  { value: 'masters', label: "Master's Degree" },
  { value: 'professional', label: 'Professional Degree' },
  { value: 'doctorate', label: 'Doctorate / PhD' },
  { value: 'post_doctorate', label: 'Post-Doctorate' },
  { value: 'self_taught', label: 'Self-taught' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * School / 12th board options — central, international, and state boards.
 * Specific state boards are rolled up under `state_board` since there are
 * 30+ of them; users pick the generic option and can supply the specific
 * board name in the school-name / institution text field.
 */
export const boardOptions = [
  { value: 'cbse', label: 'CBSE' },
  { value: 'icse', label: 'ICSE (Class 10, CISCE)' },
  { value: 'isc', label: 'ISC (Class 12, CISCE)' },
  { value: 'ib', label: 'IB (International Baccalaureate)' },
  { value: 'igcse', label: 'Cambridge IGCSE (Class 10)' },
  { value: 'a_level', label: 'Cambridge A-Level / AS-Level' },
  { value: 'nios', label: 'NIOS (National Open School)' },
  { value: 'state_board', label: 'State Board' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Subjects — common subjects across Indian school boards (10th + 12th).
 * Flat list for multi-select. Grouped in comments by domain for readability.
 */
export const subjectOptions = [
  // Languages
  { value: 'english', label: 'English' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'sanskrit', label: 'Sanskrit' },
  { value: 'regional_language', label: 'Regional Language' },
  { value: 'foreign_language', label: 'Foreign Language (French / Spanish / German)' },
  // Math & Sciences
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'physics', label: 'Physics' },
  { value: 'chemistry', label: 'Chemistry' },
  { value: 'biology', label: 'Biology' },
  { value: 'science_combined', label: 'Science (Combined — 10th)' },
  { value: 'computer_science', label: 'Computer Science / IT' },
  { value: 'informatics_practices', label: 'Informatics Practices' },
  // Commerce
  { value: 'accountancy', label: 'Accountancy' },
  { value: 'business_studies', label: 'Business Studies' },
  { value: 'economics', label: 'Economics' },
  { value: 'statistics', label: 'Statistics' },
  // Humanities
  { value: 'history', label: 'History' },
  { value: 'geography', label: 'Geography' },
  { value: 'political_science', label: 'Political Science' },
  { value: 'psychology', label: 'Psychology' },
  { value: 'sociology', label: 'Sociology' },
  { value: 'philosophy', label: 'Philosophy' },
  { value: 'social_studies', label: 'Social Studies (Combined — 10th)' },
  // Others
  { value: 'physical_education', label: 'Physical Education' },
  { value: 'fine_arts', label: 'Fine Arts' },
  { value: 'music', label: 'Music' },
  { value: 'home_science', label: 'Home Science' },
  { value: 'entrepreneurship', label: 'Entrepreneurship' },
  { value: 'legal_studies', label: 'Legal Studies' },
  { value: 'environmental_science', label: 'Environmental Science' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Stream / course options for Class 12 (or equivalent).
 */
export const streamOptions = [
  { value: 'science_pcm', label: 'Science (PCM — Physics, Chemistry, Math)' },
  { value: 'science_pcb', label: 'Science (PCB — Physics, Chemistry, Biology)' },
  { value: 'science_pcmb', label: 'Science (PCMB)' },
  { value: 'commerce_with_math', label: 'Commerce (with Mathematics)' },
  { value: 'commerce_without_math', label: 'Commerce (without Mathematics)' },
  { value: 'arts_humanities', label: 'Arts / Humanities' },
  { value: 'vocational', label: 'Vocational' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Common Indian degree names — used in diploma / graduation /
 * post-graduation / doctorate / post-doctorate repeaters.
 * Grouped by level in comments.
 */
export const degreeNameOptions = [
  // Diploma / ITI
  { value: 'diploma_polytechnic', label: 'Diploma (Polytechnic)' },
  { value: 'iti', label: 'ITI' },
  { value: 'pg_diploma', label: 'PG Diploma' },
  { value: 'pgdm', label: 'PGDM' },
  { value: 'advanced_diploma', label: 'Advanced Diploma' },
  // Undergraduate (Bachelor's)
  { value: 'ba', label: 'BA (Bachelor of Arts)' },
  { value: 'bsc', label: 'BSc (Bachelor of Science)' },
  { value: 'bcom', label: 'BCom (Bachelor of Commerce)' },
  { value: 'bba', label: 'BBA (Bachelor of Business Admin.)' },
  { value: 'bca', label: 'BCA (Bachelor of Computer Applications)' },
  { value: 'btech', label: 'BTech / BE (Engineering)' },
  { value: 'barch', label: 'BArch (Architecture)' },
  { value: 'mbbs', label: 'MBBS (Medicine)' },
  { value: 'bds', label: 'BDS (Dental)' },
  { value: 'bams', label: 'BAMS (Ayurveda)' },
  { value: 'bhms', label: 'BHMS (Homeopathy)' },
  { value: 'llb', label: 'LLB (Law)' },
  { value: 'bed', label: 'BEd (Education)' },
  { value: 'bpharm', label: 'BPharm (Pharmacy)' },
  { value: 'bhm', label: 'BHM (Hotel Management)' },
  { value: 'bfa', label: 'BFA (Fine Arts)' },
  { value: 'bvsc', label: 'BVSc (Veterinary)' },
  // Postgraduate (Master's)
  { value: 'ma', label: 'MA (Master of Arts)' },
  { value: 'msc', label: 'MSc (Master of Science)' },
  { value: 'mcom', label: 'MCom (Master of Commerce)' },
  { value: 'mba', label: 'MBA (Master of Business Admin.)' },
  { value: 'mca', label: 'MCA (Master of Computer Applications)' },
  { value: 'mtech', label: 'MTech / ME (Engineering)' },
  { value: 'md', label: 'MD (Medicine — Postgrad)' },
  { value: 'ms', label: 'MS (Surgery — Postgrad)' },
  { value: 'llm', label: 'LLM (Law — Postgrad)' },
  { value: 'med', label: 'MEd (Education — Postgrad)' },
  { value: 'mpharm', label: 'MPharm (Pharmacy — Postgrad)' },
  { value: 'msw', label: 'MSW (Social Work)' },
  { value: 'march', label: 'MArch (Architecture — Postgrad)' },
  // Doctorate / higher
  { value: 'phd', label: 'PhD' },
  { value: 'dlitt', label: 'DLitt' },
  { value: 'dsc', label: 'DSc' },
  { value: 'mds', label: 'MDS (Dental Surgery)' },
  { value: 'dm', label: 'DM (Super-specialty Medicine)' },
  { value: 'mch', label: 'MCh (Super-specialty Surgery)' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Degree options filtered by education level — so the repeater shows only
 * relevant degrees (e.g., diplomas don't show PhD, graduations don't show ITI).
 * 'other' is always included.
 */
export const DEGREE_VALUES_BY_LEVEL: Record<string, readonly string[]> = {
  diplomas: ['diploma_polytechnic', 'iti', 'pg_diploma', 'pgdm', 'advanced_diploma', 'other'],
  graduations: [
    'ba',
    'bsc',
    'bcom',
    'bba',
    'bca',
    'btech',
    'barch',
    'mbbs',
    'bds',
    'bams',
    'bhms',
    'llb',
    'bed',
    'bpharm',
    'bhm',
    'bfa',
    'bvsc',
    'other',
  ],
  postGraduations: [
    'ma',
    'msc',
    'mcom',
    'mba',
    'mca',
    'mtech',
    'md',
    'ms',
    'llm',
    'med',
    'mpharm',
    'msw',
    'march',
    'pg_diploma',
    'pgdm',
    'other',
  ],
  doctorates: ['phd', 'dlitt', 'dsc', 'mds', 'dm', 'mch', 'other'],
  postDoctorates: ['phd', 'dlitt', 'dsc', 'other'],
} as const;

export const education: FieldGroup = {
  key: 'education',
  label: 'Education',
  step: 6,
  description: 'Academic background and qualifications.',
  fields: [
    {
      order: 1,
      key: 'highestQualification',
      label: 'Highest Qualification',
      type: 'single_select',
      step: 6,
      isOnboarding: true,
      options: [...qualificationOptions],
    },
    {
      order: 2,
      key: 'education10th',
      label: 'Class 10 Details',
      type: 'object',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: {
        // Everyone who reached at least Class 10 (i.e. not below_10th / the
        // non-academic self_taught / other / prefer_not_to_say).
        field: 'highestQualification',
        values: [
          'class_10',
          'class_12',
          'iti_vocational',
          'diploma',
          'in_college',
          'associate',
          'bachelors',
          'in_grad_school',
          'masters',
          'professional',
          'doctorate',
          'post_doctorate',
        ],
      },
    },
    {
      order: 3,
      key: 'education12th',
      label: 'Class 12 Details',
      type: 'object',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: {
        // Class 12 and above.
        field: 'highestQualification',
        values: [
          'class_12',
          'iti_vocational',
          'diploma',
          'in_college',
          'associate',
          'bachelors',
          'in_grad_school',
          'masters',
          'professional',
          'doctorate',
          'post_doctorate',
        ],
      },
    },
    {
      order: 4,
      key: 'diplomas',
      label: 'Diploma',
      type: 'object_array',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'highestQualification', values: ['iti_vocational', 'diploma'] },
    },
    {
      order: 5,
      key: 'graduations',
      label: 'Graduation',
      type: 'object_array',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: {
        // Any bachelor-level path (incl. in-progress / associate / professional) and above.
        field: 'highestQualification',
        values: [
          'in_college',
          'associate',
          'bachelors',
          'professional',
          'in_grad_school',
          'masters',
          'doctorate',
          'post_doctorate',
        ],
      },
    },
    {
      order: 6,
      key: 'postGraduations',
      label: 'Post Graduation',
      type: 'object_array',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: {
        field: 'highestQualification',
        values: ['in_grad_school', 'masters', 'doctorate', 'post_doctorate'],
      },
    },
    {
      order: 7,
      key: 'doctorates',
      label: 'Doctorate / PhD',
      type: 'object_array',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'highestQualification', values: ['doctorate', 'post_doctorate'] },
    },
    {
      order: 8,
      key: 'postDoctorates',
      label: 'Post Doctorate',
      type: 'object_array',
      step: 6,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'highestQualification', values: ['post_doctorate'] },
    },
    {
      order: 9,
      key: 'otherCertificates',
      label: 'Other Certificates',
      type: 'object_array',
      step: 6,
      isOnboarding: true,
      optional: true,
    },
  ],
};
